import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { createTask, fetchUsers } from '../../utils/api/api';
import toast from 'react-hot-toast';

// Lucide Icons
import {
    User,
    Calendar,
    Flag,
    CheckCircle,
    Loader2,
    ClipboardList,
    AlignLeft
} from 'lucide-react';

const FormField = ({ label, children, icon }) => (
    <div className="mb-4">
        <label className="text-gray-700 text-sm font-medium mb-2 flex items-center">
            {icon && <span className="mr-2 text-gray-500">{icon}</span>}
            {label}
        </label>
        {children}
    </div>
);

const TaskForm = ({ onAddTask }) => {
    const [taskData, setTaskData] = useState({
        assigned_to: '',
        title: '',
        description: '',
        due_date: '',
        completion_date: '',
        priority: '',
        status: '',
    });
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        const getUsers = async () => {
            try {
                const response = await fetchUsers();
                setUsers(response.users);
            } catch (error) {
                console.error('Error fetching users:', error);
                toast.error('Failed to load users');
            }
        };

        getUsers();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setTaskData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await createTask(taskData);
            toast.success("Task added successfully");

            // Reset form
            setTaskData({
                assigned_to: '',
                title: '',
                description: '',
                due_date: '',
                completion_date: '',
                priority: '',
                status: '',
            });

            // Call the callback if provided
            if (onAddTask) {
                onAddTask(response.task);
            }
        } catch (error) {
            toast.error(error.message || "Failed to add task");
        } finally {
            setLoading(false);
        }
    };

    const inputStyles = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm";
    const selectStyles = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236b7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_10px_center] bg-no-repeat pr-10";

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
                label="Assigned To"
                icon={<User size={16} />}
            >
                <select
                    name="assigned_to"
                    value={taskData.assigned_to}
                    onChange={handleChange}
                    className={selectStyles}
                    required
                >
                    <option value="">Select team member</option>
                    {users.map((user) => (
                        <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                        </option>
                    ))}
                </select>
            </FormField>

            <FormField
                label="Task Title"
                icon={<ClipboardList size={16} />}
            >
                <input
                    type="text"
                    name="title"
                    value={taskData.title}
                    onChange={handleChange}
                    placeholder="Enter task title"
                    className={inputStyles}
                    required
                />
            </FormField>

            <FormField
                label="Description"
                icon={<AlignLeft size={16} />}
            >
                <textarea
                    name="description"
                    value={taskData.description}
                    onChange={handleChange}
                    placeholder="Describe the task details"
                    className={`${inputStyles} min-h-[100px] resize-none`}
                    required
                />
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                    label="Due Date"
                    icon={<Calendar size={16} />}
                >
                    <input
                        type="date"
                        name="due_date"
                        value={taskData.due_date}
                        onChange={handleChange}
                        className={inputStyles}
                        required
                    />
                </FormField>

                <FormField
                    label="Completion Date"
                    icon={<Calendar size={16} />}
                >
                    <input
                        type="date"
                        name="completion_date"
                        value={taskData.completion_date}
                        onChange={handleChange}
                        className={inputStyles}
                    />
                </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                    label="Priority"
                    icon={<Flag size={16} />}
                >
                    <select
                        name="priority"
                        value={taskData.priority}
                        onChange={handleChange}
                        className={selectStyles}
                        required
                    >
                        <option value="">Select priority</option>
                        <option value="hot">High</option>
                        <option value="warm">Medium</option>
                        <option value="cold">Low</option>
                    </select>
                </FormField>

                <FormField
                    label="Status"
                    icon={<CheckCircle size={16} />}
                >
                    <select
                        name="status"
                        value={taskData.status}
                        onChange={handleChange}
                        className={selectStyles}
                        required
                    >
                        <option value="">Select status</option>
                        <option value="not_started">Not Started</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="on_hold">On Hold</option>
                    </select>
                </FormField>
            </div>

            <div className="flex justify-end pt-2">
                <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`
                        flex items-center justify-center px-6 py-2 text-[14px] 
                        ${loading ? 'bg-yellow-400' : 'bg-secondary hover:bg-yellow-700'} 
                        text-white font-medium rounded-xl shadow-sm transition-colors
                        min-w-[120px]
                    `}
                >
                    {loading ? (
                        <>
                            <Loader2 size={18} className="mr-2 animate-spin" />
                            Creating...
                        </>
                    ) : (
                        'Create Task'
                    )}
                </motion.button>
            </div>
        </form>
    );
};

export default TaskForm;